import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { GridModule, GridDataResult, PageChangeEvent, SortSettings } from '@progress/kendo-angular-grid';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { DrawingNumberResult } from '../../models/file-vault.models';

@Component({
  selector: 'fv-drawing-number-list',
  standalone: true,
  template: `
    <div class="drawing-list">
      <div class="header">
        <div>
          <h3>Drawing Numbers</h3>
          <p class="subtitle" *ngIf="rtaNum">RTA {{ rtaNum }}</p>
        </div>
        <button kendoButton (click)="load()" [disabled]="!rtaNum">Refresh</button>
      </div>

      <div class="empty" *ngIf="!rtaNum">Enter an RTA number to load drawing numbers.</div>
      <div class="empty" *ngIf="rtaNum && !loading && results.length === 0">No drawing numbers found.</div>

      <div class="list" *ngIf="results.length > 0">
        <kendo-grid  *ngIf="results.length > 0"
            [data]="results"
            [resizable]="true"
            [sortable]="true">
  
  <kendo-grid-column 
    field="drawingNumber" 
    title="Drawing Number">
  </kendo-grid-column>
  
  <kendo-grid-column 
    field="description" 
    title="Description">
    <ng-template kendoGridCellTemplate let-dataItem>
      <ng-container *ngIf="editingId === dataItem.drawingLogId; else viewDescription">
        <input
          class="description-input"
          [(ngModel)]="editedDescription"
          [disabled]="savingId === dataItem.drawingLogId"
          type="text" />
      </ng-container>
      <ng-template #viewDescription>
        {{ dataItem.description }}
      </ng-template>
    </ng-template>
  </kendo-grid-column>

  <kendo-grid-column 
    field="isCreatedInSap" 
    title="In SAP"
    [width]="90">
    <ng-template kendoGridCellTemplate let-dataItem>
      <span class="sap-flag" [class.sap-yes]="dataItem.isCreatedInSap">
        {{ dataItem.isCreatedInSap ? 'Yes' : 'No' }}
      </span>
    </ng-template>
  </kendo-grid-column>
  
  <kendo-grid-column 
    field="createDate" 
    title="Created"
    format="{0:MM/dd/yyyy}">
  </kendo-grid-column>

  <kendo-grid-column title="Actions" [width]="220">
    <ng-template kendoGridCellTemplate let-dataItem>
      <ng-container *ngIf="editingId !== dataItem.drawingLogId; else editActions">
        <button kendoButton size="small" (click)="startEdit(dataItem)">Edit</button>
        <button kendoButton size="small" themeColor="warning" (click)="unlink(dataItem)" [disabled]="unlinkingId === dataItem.drawingLogId">
          Unlink
        </button>
      </ng-container>
      <ng-template #editActions>
        <button kendoButton size="small" themeColor="primary" (click)="saveEdit(dataItem)" [disabled]="savingId === dataItem.drawingLogId">
          Save
        </button>
        <button kendoButton size="small" (click)="cancelEdit()" [disabled]="savingId === dataItem.drawingLogId">
          Cancel
        </button>
      </ng-template>
    </ng-template>
  </kendo-grid-column>
  
        </kendo-grid>
      </div>

      <div class="error" *ngIf="errorMessage">{{ errorMessage }}</div>
    </div>
  `,
  styles: [`
    .drawing-list {
      padding: 16px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .header h3 {
      margin: 0 0 4px 0;
      font-size: 20px;
    }

    .subtitle {
      margin: 0;
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .description-input {
      width: 100%;
      padding: 4px 6px;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      font-size: 12px;
    }

    .sap-flag {
      font-size: 12px;
      font-weight: 600;
      color: var(--fv-text-muted);
    }

    .sap-flag.sap-yes {
      color: var(--fv-status-success-fg);
    }

    .row {
      display: grid;
      grid-template-columns: 180px 1fr 120px;
      gap: 12px;
      padding: 10px 12px;
      border-bottom: 1px solid var(--fv-border-subtle);
      font-size: 13px;
      min-width: 520px;
    }

    .row:last-child {
      border-bottom: none;
    }

    .header-row {
      background: var(--fv-surface-alt);
      font-weight: 600;
      color: var(--fv-text);
    }

    .number {
      font-weight: 600;
      color: var(--fv-text);
    }

    .description {
      color: var(--fv-text-secondary);
    }

    .date {
      color: var(--fv-text-muted);
    }

    .empty {
      font-size: 13px;
      color: var(--fv-text-muted);
      padding: 12px 0;
    }

    .error {
      margin-top: 10px;
      color: var(--fv-status-error-fg);
      font-size: 12px;
    }

    @media (max-width: 720px) {
      .row {
        grid-template-columns: 1fr;
      }
    }
  `],
  imports: [CommonModule, FormsModule, ButtonsModule, GridModule]
})
export class DrawingNumberListComponent implements OnChanges {
  private api = inject(FileVaultApiService);

  @Input() rtaNum?: number;
  @Output() drawingUnlinked = new EventEmitter<DrawingNumberResult>();

  results: DrawingNumberResult[] = [];
  loading = false;
  errorMessage = '';
  editingId: number | null = null;
  editedDescription = '';
  savingId: number | null = null;
  unlinkingId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rtaNum'] && this.rtaNum) {
      this.load();
    }
  }

  load(): void {
    if (!this.rtaNum) return;
    this.loading = true;
    this.errorMessage = '';

    this.api.getDrawingNumbersByRta(this.rtaNum).subscribe({
      next: results => {
        this.results = results;
        this.loading = false;
      },
      error: err => {
        this.errorMessage = err?.message || 'Failed to load drawing numbers';
        this.loading = false;
      }
    });
  }

  startEdit(item: DrawingNumberResult): void {
    this.editingId = item.drawingLogId;
    this.editedDescription = item.description ?? '';
    this.errorMessage = '';
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editedDescription = '';
  }

  saveEdit(item: DrawingNumberResult): void {
    if (this.savingId) return;
    const description = this.editedDescription.trim();
    if (!description) {
      this.errorMessage = 'Description is required.';
      return;
    }

    this.savingId = item.drawingLogId;
    this.api.updateDrawingLog(item.drawingLogId, { description }).subscribe({
      next: updated => {
        this.results = this.results.map(result =>
          result.drawingLogId === updated.drawingLogId ? updated : result
        );
        this.savingId = null;
        this.cancelEdit();
      },
      error: err => {
        this.errorMessage = err?.message || 'Failed to update drawing description';
        this.savingId = null;
      }
    });
  }

  unlink(item: DrawingNumberResult): void {
    if (this.unlinkingId) return;
    this.unlinkingId = item.drawingLogId;
    this.errorMessage = '';

    this.api.updateDrawingLog(item.drawingLogId, { clearRta: true }).subscribe({
      next: () => {
        this.results = this.results.filter(result => result.drawingLogId !== item.drawingLogId);
        this.unlinkingId = null;
        this.drawingUnlinked.emit(item);
      },
      error: err => {
        this.errorMessage = err?.message || 'Failed to unlink drawing from RTA';
        this.unlinkingId = null;
      }
    });
  }

  /** Remove a drawing from the local list by document number (called externally when document-links unlinks) */
  removeByDocumentNumber(documentNumber: string): void {
    this.results = this.results.filter(
      r => r.drawingNumber?.toLowerCase() !== documentNumber.toLowerCase()
    );
  }
}
